/**
 * Savings Calculator — interactive "your monthly savings" widget.
 *
 * Shoppable, embeddable, and HIGHLY linkable. Bloggers & Reddit quote
 * the numbers, which earns organic backlinks. Hosted at /calculator.
 */
import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { TrendingDown, ArrowRight, Calculator } from 'lucide-react';

// Per-transaction cost structure (card-present, average US restaurant mix)
const COMPETITORS = [
  { key: 'flavorfeed', name: 'FlavorFeed + Helcim', rate: 0.0249, perTxn: 0.08, monthlyFees: 0, color: 'emerald' },
  { key: 'toast', name: 'Toast POS', rate: 0.0249, perTxn: 0.15, monthlyFees: 69, color: 'orange' },
  { key: 'square', name: 'Square for Restaurants', rate: 0.026, perTxn: 0.10, monthlyFees: 60, color: 'blue' },
  { key: 'clover', name: 'Clover POS', rate: 0.026, perTxn: 0.10, monthlyFees: 115, color: 'emerald' },
  { key: 'lightspeed', name: 'Lightspeed Restaurant', rate: 0.026, perTxn: 0.10, monthlyFees: 69, color: 'red' },
  { key: 'stripe', name: 'FlavorFeed + Stripe', rate: 0.027, perTxn: 0.05, monthlyFees: 0, color: 'indigo' },
];

const SavingsCalculator = () => {
  const [volume, setVolume] = useState(30000);
  const [avgTicket, setAvgTicket] = useState(30);
  const [locations, setLocations] = useState(1);

  const results = useMemo(() => {
    const txnsPerMo = volume / avgTicket;
    return COMPETITORS.map((p) => {
      const processingFees = volume * p.rate + txnsPerMo * p.perTxn;
      const totalMo = (processingFees + p.monthlyFees) * locations;
      return { ...p, processingFees, totalMo, annualCost: totalMo * 12 };
    }).sort((a, b) => a.totalMo - b.totalMo);
  }, [volume, avgTicket, locations]);

  const cheapest = results[0];
  const mostExpensive = results[results.length - 1];
  const annualSavings = (mostExpensive.totalMo - cheapest.totalMo) * 12;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Helmet>
        <title>Restaurant POS Cost Calculator — Compare FlavorFeed vs Toast, Square, Clover, Lightspeed</title>
        <meta name="description" content="Enter your monthly card volume and see exactly how much you'll save with FlavorFeed vs Toast, Square, Clover, or Lightspeed. Real-time cost calculator — free, no signup." />
        <link rel="canonical" href="https://www.flavorfeed.ai/calculator" />
        <meta property="og:title" content="Restaurant POS Cost Calculator — Compare in 30 seconds" />
        <meta property="og:description" content="See how much you'll save per month switching POS. Interactive calculator — real numbers." />
        <meta property="og:url" content="https://www.flavorfeed.ai/calculator" />
      </Helmet>

      <section className="px-6 pt-16 pb-6">
        <div className="mx-auto max-w-5xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-200">
            <Calculator className="h-3 w-3" /> Free tool · No signup
          </div>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">How much are you overpaying for POS?</h1>
          <p className="mt-3 text-lg text-slate-300">Enter your monthly card volume. See the cost for every popular restaurant POS, side by side.</p>
        </div>
      </section>

      {/* Inputs */}
      <section className="px-6 py-6">
        <div className="mx-auto max-w-5xl rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <SliderInput label="Monthly card volume" value={volume} setValue={setVolume} min={5000} max={200000} step={1000} format={(v) => `$${v.toLocaleString()}`} />
            <SliderInput label="Average ticket size" value={avgTicket} setValue={setAvgTicket} min={8} max={120} step={1} format={(v) => `$${v}`} />
            <SliderInput label="Number of locations" value={locations} setValue={setLocations} min={1} max={25} step={1} format={(v) => `${v}`} />
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="px-6 py-8">
        <div className="mx-auto max-w-5xl">
          {/* Savings headline */}
          <div className="mb-6 rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-900/30 to-teal-900/20 p-6 text-center">
            <div className="inline-flex items-center gap-2 text-emerald-300">
              <TrendingDown className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">You could save up to</span>
            </div>
            <div className="mt-2 text-5xl font-black text-white sm:text-6xl">
              ${Math.round(annualSavings).toLocaleString()}
              <span className="ml-2 text-xl text-emerald-300">/year</span>
            </div>
            <p className="mt-2 text-slate-300">
              Switching from <strong>{mostExpensive.name}</strong> to <strong>{cheapest.name}</strong> on your ${(volume * locations).toLocaleString()}/month card volume.
            </p>
          </div>

          {/* Leaderboard */}
          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 border-b border-slate-800 bg-slate-900/80 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <div>#</div>
              <div>Processor</div>
              <div className="text-right">Monthly</div>
              <div className="text-right">Annual</div>
            </div>
            {results.map((r, i) => (
              <div
                key={r.key}
                className={`grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 border-b border-slate-800/60 px-5 py-4 transition ${
                  i === 0 ? 'bg-emerald-500/10' : i === results.length - 1 ? 'bg-rose-500/5' : ''
                }`}
              >
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${i === 0 ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-400'}`}>
                  {i + 1}
                </div>
                <div>
                  <div className="text-base font-semibold text-white">
                    {r.name}
                    {r.key === 'flavorfeed' && <span className="ml-2 inline-block rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300">CHEAPEST</span>}
                  </div>
                  <div className="text-xs text-slate-400">
                    {(r.rate * 100).toFixed(2)}% + ${r.perTxn.toFixed(2)}/txn
                    {r.monthlyFees > 0 ? ` + $${r.monthlyFees}/mo software` : ' · $0 software'}
                  </div>
                </div>
                <div className="text-right text-base font-semibold text-white">
                  ${Math.round(r.totalMo).toLocaleString()}
                </div>
                <div className="text-right text-sm text-slate-300">
                  ${Math.round(r.annualCost).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-[11px] text-slate-500">
            * Estimates based on US card-present blended rate (~70% credit / 30% debit). Actual rates vary by processor agreement, card mix, and volume tier. Helcim&apos;s interchange-plus pricing becomes even cheaper at higher volumes; Stripe&apos;s flat rate stays constant. Software fees are baseline published prices; negotiation and add-ons not included.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-3xl rounded-3xl border border-violet-500/40 bg-gradient-to-br from-violet-600/20 via-fuchsia-600/10 to-transparent p-10 text-center">
          <h2 className="text-3xl font-bold">Want to capture ${Math.round(annualSavings).toLocaleString()}/year for your restaurant?</h2>
          <p className="mt-3 text-slate-300">Start free on FlavorFeed — we&apos;ll help you migrate and save.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/signup?plan=free"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3.5 text-base font-semibold shadow-lg shadow-violet-900/50 transition hover:from-violet-500 hover:to-fuchsia-500"
              data-testid="calc-cta-signup"
            >
              Start Free in 2 Minutes <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/vs/toast"
              className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-800/50 px-7 py-3.5 text-base font-semibold text-slate-100 transition hover:border-slate-500"
            >
              See FlavorFeed vs Toast
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const SliderInput = ({ label, value, setValue, min, max, step, format }) => (
  <div>
    <label className="mb-2 block text-sm font-semibold text-slate-300">{label}</label>
    <div className="flex items-baseline justify-between">
      <span className="text-3xl font-bold text-white">{format(value)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => setValue(Number(e.target.value))}
      className="mt-3 w-full accent-violet-500"
      data-testid={`calc-${label.toLowerCase().replace(/\s+/g, '-')}`}
    />
    <div className="mt-1 flex justify-between text-[10px] text-slate-500">
      <span>{format(min)}</span>
      <span>{format(max)}</span>
    </div>
  </div>
);

export default SavingsCalculator;
